import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { RootState } from '../../store';
import {
  useGetTcSchoolClassesQuery,
  useLazyGetCurrentStudentsQuery,
  useLazyGetPastStudentsQuery,
  type TcStudentRow,
} from './StudentTcDetails.service';
import StudentEditForm from './StudentEditForm';
import StudentTransfer from './StudentTransfer';
import StudentBonafide from './StudentBonafide';
import { ENV_CONFIG } from '../../shared/config/env.config';
import './student-tc-details.css';

interface Option {
  label: string;
  value: string | number;
}

// Stage of the page. Phase 1 implements 'list'; the others are placeholders
// that later phases (edit form, transfer flow, bonafide certificate) fill in.
type Stage = 'list' | 'edit' | 'transfer' | 'bonafide';

// class_id → Roman/short label (matches the Angular classList mapping).
const CLASS_LABELS: Record<string, string> = {
  '1': 'I',
  '2': 'II',
  '3': 'III',
  '4': 'IV',
  '5': 'V',
  '6': 'VI',
  '7': 'VII',
  '8': 'VIII',
  '9': 'IX',
  '10': 'X',
  '11': 'XI',
  '12': 'XII',
  '13': 'LKG',
  '14': 'UKG',
  '15': 'PreKG',
};

const CATEGORY_OPTIONS: Option[] = [
  { label: 'Current Student List', value: 0 },
  { label: 'Past Student List', value: 1 },
];

// dd-MM-yyyy, matching Angular's DatePipe 'dd-MM-yyyy'.
const formatDate = (value?: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
};

// Sort by gender then name (Angular's compareObjects).
const compareStudents = (a: TcStudentRow, b: TcStudentRow): number => {
  const ag = String(a.gender ?? '');
  const bg = String(b.gender ?? '');
  if (ag < bg) return -1;
  if (ag > bg) return 1;
  const an = String(a.name ?? '');
  const bn = String(b.name ?? '');
  if (an < bn) return -1;
  if (an > bn) return 1;
  return 0;
};

const StudentTcDetails: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const schoolId = s.schoolId;
  const schoolName = s.schoolName;
  const schoolTypeId = s.schoolTypeId;
  const userName = s.username;
  const [searchParams, setSearchParams] = useSearchParams();

  const toast = useRef<Toast>(null);
  const notify = (
    severity: 'success' | 'error' | 'warn' | 'info',
    detail: string
  ): void => toast.current?.show({ severity, detail, life: 4000 });

  // On return from the CDAC e-sign service, download the signed bonafide PDF
  // (Angular esignBonafideCheck). esignStatus=2 → download; esignStatus=1 → error.
  useEffect(() => {
    const esignStatus = searchParams.get('esignStatus');
    const id = searchParams.get('id');
    if (!esignStatus) return;

    (async () => {
      if (esignStatus === '2' && id) {
        try {
          const resp = await fetch(
            `${ENV_CONFIG.g2cUrl}/api/esevaiBonafideDownload?filename=${id}`
          );
          const blob = await resp.blob();
          if (blob.type === 'application/json') {
            notify(
              'error',
              'Unable to download the certificate. Please try again!'
            );
          } else {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bonafide_${id}`;
            link.click();
            window.URL.revokeObjectURL(url);
            notify('success', 'Bonafide Certificate downloaded successfully.');
          }
        } catch {
          notify(
            'error',
            'Unable to download the certificate. Please try again!'
          );
        }
      } else if (esignStatus === '1') {
        notify(
          'error',
          'We are facing an issue in e-signing. Please try again later.'
        );
      }
      // Clear the e-sign query params so a refresh doesn't re-trigger.
      setSearchParams({}, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── page state ──────────────────────────────────────────────────────────────
  const [stage, setStage] = useState<Stage>('list');
  const [selectedRow, setSelectedRow] = useState<TcStudentRow | null>(null);

  const [category, setCategory] = useState<number>(0);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');

  const [currentStudentList, setCurrentStudentList] = useState<TcStudentRow[]>(
    []
  );
  const [pastStudentList, setPastStudentList] = useState<TcStudentRow[]>([]);
  const [studentList, setStudentList] = useState<TcStudentRow[]>([]);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  // ── data ────────────────────────────────────────────────────────────────────
  const { data: classesResp } = useGetTcSchoolClassesQuery(
    { schoolId },
    { skip: !s.loaded || !schoolId }
  );
  const [getCurrentStudents] = useLazyGetCurrentStudentsQuery();
  const [getPastStudents] = useLazyGetPastStudentsQuery();

  // Raw class/section pairs from the API.
  const classAndSection = useMemo(
    () => classesResp?.result ?? [],
    [classesResp]
  );

  // Distinct class options with Roman labels.
  const classList = useMemo<Option[]>(() => {
    const seen = new Set<string>();
    const opts: Option[] = [];
    classAndSection.forEach((cs) => {
      if (!seen.has(cs.Cls)) {
        seen.add(cs.Cls);
        opts.push({ value: cs.Cls, label: CLASS_LABELS[cs.Cls] ?? cs.Cls });
      }
    });
    return opts;
  }, [classAndSection]);

  // Sections available for the selected class.
  const sectionFilter = useMemo<Option[]>(
    () =>
      classAndSection
        .filter((cs) => cs.Cls === selectedClass)
        .map((cs) => ({ label: cs.section, value: cs.section })),
    [classAndSection, selectedClass]
  );

  // Swap the visible list when the category toggles (Angular onSingleButtonChange).
  const applyCategory = (cat: number): void => {
    const list = cat === 0 ? currentStudentList : pastStudentList;
    setStudentList([...list].sort(compareStudents));
  };

  const handleCategoryChange = (cat: number | null): void => {
    if (cat === null || cat === undefined) return;
    setCategory(cat);
    applyCategory(cat);
  };

  const handleClassChange = (cls: string): void => {
    setSelectedClass(cls);
    setSelectedSection('');
    setStudentList([]);
  };

  // Get Student List (Angular handleClassFilter).
  const handleSubmit = async (): Promise<void> => {
    if (!selectedClass || !selectedSection) return;
    try {
      if (category === 0) {
        const res = await getCurrentStudents({
          cls: selectedClass,
          sec: selectedSection,
        }).unwrap();
        if (res.result) {
          const rows = res.result.map((item) => ({
            ...item,
            tc_status: item.DateFlag === '1' ? 'Updated' : 'Not Updated',
            formatted_last_date:
              item.DateFlag === '1' ? formatDate(item.student_tc_date) : '',
            class_and_section: `${item.class_studying ?? ''}-${
              item.class_section ?? ''
            }`,
          }));
          setCurrentStudentList(rows);
          setStudentList([...rows].sort(compareStudents));
        } else {
          setCurrentStudentList([]);
          setStudentList([]);
          notify('error', res.message ?? 'No records found');
        }
      } else {
        const res = await getPastStudents({
          cls: selectedClass,
          sec: selectedSection,
        }).unwrap();
        if (res.result) {
          const rows = res.result.map((item) => ({
            ...item,
            tc_status: 'Updated',
            formatted_last_date: formatDate(item.student_tc_date),
            class_and_section: `${item.class_studying ?? ''}-${
              item.class_section ?? ''
            }`,
          }));
          setPastStudentList(rows);
          setStudentList([...rows].sort(compareStudents));
        } else {
          setPastStudentList([]);
          setStudentList([]);
          notify('error', res.message ?? 'No records found');
        }
      }
    } catch {
      notify('error', 'Unable to load the student list. Please try again.');
    }
  };

  // Academic year label for the Excel title (Angular acadYear).
  const acadYear = (): string => {
    const month = new Date().getMonth() + 1;
    const years = [new Date().getFullYear(), new Date().getFullYear() - 1];
    const tmp = years.map((date) =>
      month > 6 ? `${date}-${date + 1}` : `${date - 1}-${date}`
    );
    return category === 0 ? `Curr Yr ${tmp[0]}` : `Past Yr ${tmp[1]}`;
  };

  const handleExportExcel = async (): Promise<void> => {
    const rows = studentList.map((item) => ({
      'EMIS Id': item.unique_id_no,
      Name: item.name,
      'Name in Tamil': item.name_tamil,
      Class: item.class_studying,
      Section: item.class_section,
      'TC Details': item.tc_status,
      'TC Date': item.student_tc_date,
    }));
    const xlsx = await import('xlsx');
    const worksheet = xlsx.utils.json_to_sheet(rows);
    const workbook = {
      Sheets: { 'student`s TC status list': worksheet },
      SheetNames: ['student`s TC status list'],
    };
    xlsx.writeFile(
      workbook,
      `${schoolName || 'students'} TC ${acadYear()}.xlsx`
    );
  };

  // ── row action visibility (mirrors the Angular *ngIf conditions) ──────────────
  const showTransfer = (row: TcStudentRow): boolean =>
    (category !== 1 && row.DateFlag === '1') ||
    (category === 1 && row.mhrd_id === 114);

  const showPdf = (row: TcStudentRow): boolean =>
    !!row.student_id_in_tc_dtl && row.transfer_flag === '1';

  const showBonafide = (row: TcStudentRow): boolean => {
    const cls = Number(selectedClass);
    return (
      cls > 7 &&
      cls < 13 &&
      !!row.student_id_in_tc_dtl &&
      row.transfer_flag === '1'
    );
  };

  // Phase 1: row actions capture the row and switch stage; the target stages are
  // built in later phases.
  const handleEdit = (row: TcStudentRow): void => {
    setSelectedRow(row);
    setStage('edit');
  };
  const handleTransfer = (row: TcStudentRow): void => {
    // Angular requires the student's details to be updated before transfer.
    if (row.tc_status !== 'Updated') {
      notify('error', 'Please update this student details');
      return;
    }
    setSelectedRow(row);
    setStage('transfer');
  };
  const handlePdf = (row: TcStudentRow): void => {
    setSelectedRow(row);
    notify('info', 'TC certificate download will be added in a later phase.');
  };
  const handleBonafide = (row: TcStudentRow): void => {
    setSelectedRow(row);
    setStage('bonafide');
  };

  const goToList = (): void => {
    setStage('list');
    setSelectedRow(null);
  };

  const actionBody = (row: TcStudentRow) => (
    <div className="d-flex align-items-center justify-content-center gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-info p-button-sm"
        onClick={() => handleEdit(row)}
        tooltip="Edit"
      />
      {showTransfer(row) && (
        <Button
          icon="pi pi-arrow-circle-right"
          className="p-button-success p-button-sm"
          onClick={() => handleTransfer(row)}
          tooltip="Transfer"
        />
      )}
      {showPdf(row) && (
        <Button
          icon="pi pi-file-pdf"
          className="p-button-danger p-button-sm"
          onClick={() => handlePdf(row)}
          tooltip="TC PDF"
        />
      )}
      {showBonafide(row) && (
        <Button
          icon="pi pi-file"
          className="p-button-warning p-button-sm"
          onClick={() => handleBonafide(row)}
          tooltip="Bonafide"
        />
      )}
    </div>
  );

  return (
    <div className="main-body-content student-tc-details">
      <Toast ref={toast} />

      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div className="breadcrumbs mb-3">
          <h6 className="url m-0">
            <b>Students</b>
            <span
              className="ms-2 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={goToList}
              onKeyDown={(e) => e.key === 'Enter' && goToList()}
            >
              <i className="feather icon-chevron-right" /> Students TC Details
            </span>
            {stage === 'edit' && (
              <span className="ms-2">
                <i className="feather icon-chevron-right" /> Student Details
              </span>
            )}
            {stage === 'transfer' && (
              <span className="ms-2">
                <i className="feather icon-chevron-right" /> Verify TC Details
              </span>
            )}
            {stage === 'bonafide' && (
              <span className="ms-2">
                <i className="feather icon-chevron-right" /> Bonafide
              </span>
            )}
          </h6>
        </div>
      </div>

      {stage === 'list' && (
        <div className="card p-4">
          <SelectButton
            options={CATEGORY_OPTIONS}
            value={category}
            onChange={(e) => handleCategoryChange(e.value)}
            optionLabel="label"
            optionValue="value"
          />

          <div className="row mt-3">
            <div className="col-md-3 col-sm-12">
              <label>Class</label>
              <Dropdown
                className="w-100"
                options={classList}
                value={selectedClass || null}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Class"
                onChange={(e) => handleClassChange(e.value)}
              />
            </div>
            <div className="col-md-3 col-sm-12">
              <label>Section</label>
              <Dropdown
                className="w-100"
                options={sectionFilter}
                value={selectedSection || null}
                optionLabel="label"
                optionValue="value"
                placeholder="Select Section"
                onChange={(e) => setSelectedSection(e.value)}
              />
            </div>
            <div className="col mt-4">
              {selectedClass && selectedSection && (
                <Button
                  label="Submit"
                  className="p-button-primary"
                  onClick={handleSubmit}
                />
              )}
            </div>
            <div className="col mt-4 text-end">
              <Button
                label="Download"
                icon="pi pi-file-excel"
                className="p-button-success"
                onClick={handleExportExcel}
                disabled={studentList.length === 0}
              />
            </div>
          </div>

          <div className="student-table mt-3">
            <div className="mb-2 d-flex justify-content-end">
              <span
                className="p-input-icon-left"
                style={{ width: '100%', maxWidth: 320 }}
              >
                <i className="pi pi-search ps-1" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search"
                  className="textbox-filter ps-4"
                />
              </span>
            </div>

            <DataTable
              value={studentList}
              sortMode="single"
              size="small"
              stripedRows
              responsiveLayout="scroll"
              emptyMessage="No records found"
              globalFilter={globalFilter}
              globalFilterFields={[
                'NewEmisId',
                'name',
                'class_and_section',
                'tc_status',
                'formatted_last_date',
              ]}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            >
              <Column
                header="#"
                body={(_r, { rowIndex }) => rowIndex + 1}
                style={{ width: '60px' }}
              />
              <Column field="NewEmisId" header="EMIS ID" sortable />
              <Column field="name" header="Name" sortable />
              <Column
                field="class_and_section"
                header="Class - Section"
                sortable
              />
              <Column field="tc_status" header="TC Details" sortable />
              <Column
                field="formatted_last_date"
                header="TC issue date"
                sortable
              />
              <Column
                header="Action"
                body={actionBody}
                style={{ width: '160px' }}
                className="text-center"
              />
            </DataTable>
          </div>
        </div>
      )}

      {stage === 'edit' && selectedRow && (
        <StudentEditForm
          row={selectedRow}
          schoolId={schoolId}
          schoolTypeId={schoolTypeId}
          onBack={goToList}
          onSaved={handleSubmit}
        />
      )}

      {stage === 'transfer' && selectedRow && (
        <StudentTransfer
          row={selectedRow}
          schoolId={schoolId}
          schoolName={schoolName}
          userName={userName}
          selectedClass={selectedClass}
          onBack={goToList}
          onSaved={handleSubmit}
        />
      )}

      {stage === 'bonafide' && selectedRow && (
        <StudentBonafide
          row={selectedRow}
          schoolId={schoolId}
          schoolTypeId={schoolTypeId}
          userName={userName}
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onBack={goToList}
        />
      )}
    </div>
  );
};

export default StudentTcDetails;
